import { useState, type FormEvent } from "react";
import { Link } from "react-router";
import { Banner } from "../../components/Banner";
import { Button } from "../../components/Button";
import { Field, inputShell } from "../../components/Field";
import { NewPasswordFields, newPasswordReady } from "../../components/admin/PasswordFields";
import { ScreenHeader } from "../../components/ScreenHeader";

/** "Forgot password?": asks for the email and always says the same thing, whether or not it is an admin. */
export function ForgotPasswordScreen({
  busy,
  error,
  sent,
  onSubmit,
}: {
  busy: boolean;
  error: string | null;
  sent: boolean;
  onSubmit: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (email.trim()) onSubmit(email.trim());
  };

  return (
    <form onSubmit={submit} className="max-w-[440px]">
      <ScreenHeader
        kicker="Staff"
        title="Reset your password"
        sub="Enter your company email and we'll send you a link to choose a new password."
      />
      {sent ? (
        <Banner tone="good">
          If that's an admin account, a reset link is on its way. It works once, for 30 minutes.
          Check your spam folder if it doesn't arrive in a few minutes.
        </Banner>
      ) : (
        <>
          <Field label="Company email" htmlFor="forgot-email">
            <input
              id="forgot-email"
              type="email"
              autoComplete="username"
              className={inputShell}
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </Field>
          {error ? (
            <p role="alert" className="mb-3 text-[13.5px] text-negative">
              {error}
            </p>
          ) : null}
          <Button type="submit" disabled={busy || !email.trim()}>
            {busy ? "Sending..." : "Send reset link"}
          </Button>
        </>
      )}
      <p className="mt-5 text-[13.5px]">
        <Link to="/admin" className="font-semibold text-accent underline">
          Back to sign-in
        </Link>
      </p>
      <p className="text-[13px] text-text-muted">
        No email? A super admin can reset your password from Team.
      </p>
    </form>
  );
}

/** The page the emailed link opens. */
export function ResetPasswordScreen({
  hasToken,
  busy,
  error,
  onSubmit,
}: {
  hasToken: boolean;
  busy: boolean;
  error: string | null;
  onSubmit: (newPassword: string) => void;
}) {
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  // The email isn't known here; the server applies the "not your email" rule and reports it.
  const ready = newPasswordReady(next, confirm, "");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready) onSubmit(next);
  };

  return (
    <form onSubmit={submit} className="max-w-[440px]">
      <ScreenHeader kicker="Staff" title="Choose a new password" />
      {hasToken ? (
        <>
          <NewPasswordFields
            email=""
            value={next}
            confirm={confirm}
            onChange={setNext}
            onConfirm={setConfirm}
          />
          {error ? (
            <p role="alert" className="mb-3 text-[13.5px] text-negative">
              {error}
            </p>
          ) : null}
          <Button type="submit" disabled={busy || !ready}>
            {busy ? "Saving..." : "Save and sign in"}
          </Button>
        </>
      ) : (
        <Banner tone="bad">This link is incomplete. Open the link from the email again.</Banner>
      )}
      <p className="mt-5 text-[13.5px]">
        <Link to="/admin/forgot-password" className="font-semibold text-accent underline">
          Send a new link
        </Link>
      </p>
    </form>
  );
}
